import { useState, useEffect } from 'react';
import axios from 'axios';
import { Vote, Plus, Trash2, Edit, Users, BarChart3, Play, Square, ChevronDown, ChevronUp, X, User, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import RichTextEditor from '@/components/RichTextEditor';
import { API } from '@/App';

const STATUS_LABELS = { draft: 'Brouillon', active: 'En cours', closed: 'Terminé' };
const STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', active: 'bg-green-100 text-green-700', closed: 'bg-red-100 text-red-600' };
const TYPE_LABELS = { election: 'Élection', poll: 'Sondage' };

const ElectionsManagementPage = () => {
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resultsModal, setResultsModal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const emptyForm = {
    title: '', description: '', type: 'election',
    audience: 'national', region_slug: '',
    start_date: '', end_date: '',
    min_choices: 1, max_choices: 1, winners_count: 1, candidates: []
  };
  const [form, setForm] = useState(emptyForm);
  const [candidateForm, setCandidateForm] = useState({ name: '', photo_url: '', description: '', youtube_url: '', links: '' });
  const [regions, setRegions] = useState([]);

  useEffect(() => { fetchElections(); }, []);

  useEffect(() => {
    axios.get(`${API}/regions`).then(r => setRegions(r.data || [])).catch(() => {});
  }, []);

  const fetchElections = async () => {
    try {
      const r = await axios.get(`${API}/admin/elections`);
      setElections(r.data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Titre requis');
    if (form.candidates.length === 0) return toast.error('Ajoutez au moins un candidat');
    if (form.min_choices > form.max_choices) return toast.error('Le minimum doit être inférieur au maximum');
    if (form.audience === 'regional' && !form.region_slug) return toast.error('Sélectionnez la région concernée');
    if (form.start_date && form.end_date && new Date(form.start_date) >= new Date(form.end_date)) {
      return toast.error("La date de fin doit être après la date d'ouverture");
    }
    try {
      const payload = {
        ...form,
        // Send empty region_slug as null to avoid stale value when audience=national
        region_slug: form.audience === 'regional' ? form.region_slug : '',
      };
      if (editing) {
        await axios.put(`${API}/admin/elections/${editing}`, payload);
        toast.success('Vote modifié');
      } else {
        await axios.post(`${API}/admin/elections`, payload);
        toast.success('Vote créé');
      }
      setShowForm(false); setEditing(null); setForm(emptyForm);
      fetchElections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce vote ?')) return;
    try { await axios.delete(`${API}/admin/elections/${id}`); fetchElections(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/elections/${id}`, { status: newStatus });
      fetchElections();
      toast.success(newStatus === 'active' ? 'Vote ouvert' : newStatus === 'closed' ? 'Vote clôturé' : 'Statut modifié');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const viewResults = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/elections/${id}/results`);
      setResultsModal(r.data);
    } catch { toast.error('Erreur de chargement des résultats'); }
  };

  const downloadReport = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/elections/${id}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Erreur de téléchargement'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_vote_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport téléchargé');
    } catch { toast.error('Erreur'); }
  };

  const addCandidate = () => {
    if (!candidateForm.name.trim()) return toast.error('Nom du candidat requis');
    setForm(f => ({ ...f, candidates: [...f.candidates, { ...candidateForm, id: Date.now().toString() }] }));
    setCandidateForm({ name: '', photo_url: '', description: '', youtube_url: '', links: '' });
  };

  const handleCandidatePhotoUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/cms-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCandidateForm(f => ({ ...f, photo_url: res.data.url }));
      toast.success('Photo uploadée');
    } catch { toast.error("Erreur d'upload"); }
  };

  const removeCandidate = (idx) => {
    setForm(f => ({ ...f, candidates: f.candidates.filter((_, i) => i !== idx) }));
  };

  const openEdit = (e) => {
    setForm({
      title: e.title || '',
      description: e.description || '',
      type: e.type || 'election',
      audience: e.region_slug ? 'regional' : 'national',
      region_slug: e.region_slug || '',
      start_date: e.start_date ? e.start_date.slice(0, 16) : '',
      end_date: e.end_date ? e.end_date.slice(0, 16) : '',
      min_choices: e.min_choices,
      max_choices: e.max_choices,
      winners_count: e.winners_count || 1,
      candidates: e.candidates || []
    });
    setEditing(e.id); setShowForm(true);
  };

  return (
    <AdminLayout>
    <div className="p-6 max-w-6xl mx-auto" data-testid="elections-management">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
            <Vote size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Votes & Élections</h1>
            <p className="text-xs text-gray-500">Gérez les votes internes et les élections du bureau</p>
          </div>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#35AFE4] text-white rounded-lg text-sm font-semibold hover:bg-[#1e9bd4] transition-colors"
          data-testid="create-election-btn">
          <Plus size={16} /> Nouveau vote
        </button>
      </div>

      {/* Election List */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div>
      ) : elections.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-2xl">
          <Vote size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">Aucun vote créé</p>
          <p className="text-gray-400 text-sm mt-1">Créez votre premier vote ou élection</p>
        </div>
      ) : (
        <div className="space-y-4">
          {elections.map(e => (
            <div key={e.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`election-${e.id}`}>
              <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${STATUS_COLORS[e.status]}`}>{STATUS_LABELS[e.status]}</span>
                    <span className="text-[10px] text-gray-400 mt-1">{TYPE_LABELS[e.type]}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>{e.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{e.candidates?.length || 0} candidats | {e.vote_count || 0} votes | Min: {e.min_choices} / Max: {e.max_choices}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {e.status === 'draft' && (
                    <button onClick={(ev) => { ev.stopPropagation(); handleStatusChange(e.id, 'active'); }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Ouvrir le vote">
                      <Play size={16} />
                    </button>
                  )}
                  {e.status === 'active' && (
                    <button onClick={(ev) => { ev.stopPropagation(); handleStatusChange(e.id, 'closed'); }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Clôturer">
                      <Square size={16} />
                    </button>
                  )}
                  <button onClick={(ev) => { ev.stopPropagation(); viewResults(e.id); }}
                    className="p-2 text-[#35AFE4] hover:bg-blue-50 rounded-lg transition-colors" title="Résultats">
                    <BarChart3 size={16} />
                  </button>
                  <button onClick={(ev) => { ev.stopPropagation(); downloadReport(e.id); }}
                    className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Télécharger le rapport PDF" data-testid={`download-report-${e.id}`}>
                    <Download size={16} />
                  </button>
                  <button onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}
                    className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors" title="Modifier">
                    <Edit size={16} />
                  </button>
                  <button onClick={(ev) => { ev.stopPropagation(); handleDelete(e.id); }}
                    className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                    <Trash2 size={16} />
                  </button>
                  {expandedId === e.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </div>
              {expandedId === e.id && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                  {e.description && <p className="text-sm text-gray-600 mb-4">{e.description}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(e.candidates || []).map(c => (
                      <div key={c.id} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-lg font-bold overflow-hidden flex-shrink-0 shadow-md">
                            {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" /> : c.name[0]}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{c.name}</p>
                            {c.votes !== undefined && <p className="text-xs text-[#35AFE4] font-semibold">{c.votes || 0} voix</p>}
                          </div>
                        </div>
                        {c.description && <p className="text-xs text-gray-600 mb-2 leading-relaxed">{c.description}</p>}
                        {c.youtube_url && (
                          <a href={c.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-red-600 font-semibold hover:underline mb-2">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                            Vidéo de présentation
                          </a>
                        )}
                        {c.links && <p className="text-[10px] text-gray-500">{c.links}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {editing ? 'Modifier le vote' : 'Nouveau vote'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Titre</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-[#35AFE4]"
                  placeholder="ex: Élection du bureau 2026" data-testid="election-title-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Description</label>
                <RichTextEditor value={form.description} onChange={val => setForm(f => ({ ...f, description: val }))} placeholder="Informations sur ce vote..." height="120px" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="election-type-select">
                    <option value="election">Élection</option>
                    <option value="poll">Sondage</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Choix minimum / votant</label>
                  <input type="number" min={1} value={form.min_choices} onChange={e => setForm(f => ({ ...f, min_choices: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="election-min-input" />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Choix maximum / votant</label>
                  <input type="number" min={1} value={form.max_choices} onChange={e => setForm(f => ({ ...f, max_choices: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="election-max-input" />
                </div>
              </div>

              {/* Winners count */}
              <div className="bg-emerald-50/40 border border-emerald-100 rounded-2xl p-4">
                <label className="text-xs font-bold text-emerald-700 uppercase mb-2 block">Nombre d'élus / lauréats</label>
                <div className="flex items-center gap-3">
                  <input type="number" min={1} value={form.winners_count}
                    onChange={e => setForm(f => ({ ...f, winners_count: parseInt(e.target.value) || 1 }))}
                    className="w-24 px-3 py-2 border-2 border-emerald-200 rounded-lg text-base font-bold text-center bg-white"
                    data-testid="election-winners-input" />
                  <p className="text-xs text-emerald-700 font-medium">
                    Les <strong>{form.winners_count}</strong> candidat{form.winners_count > 1 ? 's' : ''} ayant reçu le plus de voix seront marqués <strong>"élus"</strong> dans le rapport final.
                  </p>
                </div>
              </div>

              {/* Audience (national / regional) */}
              <div className="bg-blue-50/40 border border-blue-100 rounded-2xl p-4">
                <label className="text-xs font-bold text-blue-700 uppercase mb-3 block">À qui ce vote est-il ouvert ?</label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button type="button" onClick={() => setForm(f => ({ ...f, audience: 'national', region_slug: '' }))}
                    className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                      form.audience === 'national'
                        ? 'border-[#35AFE4] bg-white text-[#1e3a5f] shadow-sm'
                        : 'border-transparent bg-white/50 text-gray-500 hover:bg-white'
                    }`}
                    data-testid="audience-national">
                    🇫🇷 National<br /><span className="text-[11px] font-normal text-gray-500">Tous les ophtalmos cotisants</span>
                  </button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, audience: 'regional' }))}
                    className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                      form.audience === 'regional'
                        ? 'border-[#35AFE4] bg-white text-[#1e3a5f] shadow-sm'
                        : 'border-transparent bg-white/50 text-gray-500 hover:bg-white'
                    }`}
                    data-testid="audience-regional">
                    📍 Régional<br /><span className="text-[11px] font-normal text-gray-500">Membres d'une seule région</span>
                  </button>
                </div>
                {form.audience === 'regional' && (
                  <div>
                    <label className="text-[11px] font-bold text-blue-700 uppercase mb-1 block">Région concernée *</label>
                    <select value={form.region_slug} onChange={e => setForm(f => ({ ...f, region_slug: e.target.value }))}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white" data-testid="election-region-select">
                      <option value="">— Choisir une région —</option>
                      {regions.map(r => (
                        <option key={r.id || r.slug || r.name} value={r.name}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Date d'ouverture</label>
                  <input type="datetime-local" value={form.start_date}
                    onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="election-start-date" />
                  <p className="text-[10px] text-gray-400 mt-1">Optionnel — laissez vide pour ouvrir manuellement</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Date de fermeture</label>
                  <input type="datetime-local" value={form.end_date}
                    onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="election-end-date" />
                  <p className="text-[10px] text-gray-400 mt-1">Optionnel — laissez vide pour clôturer manuellement</p>
                </div>
              </div>

              {/* Candidates */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Candidats ({form.candidates.length})</label>
                <div className="space-y-2 mb-3">
                  {form.candidates.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-sm font-bold overflow-hidden flex-shrink-0">
                        {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" /> : c.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                        {c.description && <p className="text-[11px] text-gray-500 truncate">{c.description}</p>}
                        <div className="flex gap-2 mt-0.5">
                          {c.youtube_url && <span className="text-[10px] text-red-500 font-semibold">YouTube</span>}
                          {c.links && <span className="text-[10px] text-blue-500 font-semibold">Liens</span>}
                        </div>
                      </div>
                      <button onClick={() => removeCandidate(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                <div className="border border-dashed border-gray-300 rounded-xl p-4 space-y-3 bg-gray-50/50">
                  <p className="text-xs font-bold text-gray-500 uppercase">Ajouter un candidat</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input value={candidateForm.name} onChange={e => setCandidateForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Nom du candidat *" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="candidate-name-input" />
                    <div className="flex gap-2">
                      <input value={candidateForm.photo_url} onChange={e => setCandidateForm(f => ({ ...f, photo_url: e.target.value }))}
                        placeholder="URL photo ou uploader" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                      <label className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium cursor-pointer transition-colors flex-shrink-0">
                        <Upload size={12} />
                        <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) handleCandidatePhotoUpload(e.target.files[0]); }} />
                      </label>
                    </div>
                  </div>
                  <textarea value={candidateForm.description} onChange={e => setCandidateForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Texte de présentation du candidat..." className="w-full px-3 py-2 border rounded-lg text-sm h-20 resize-none" data-testid="candidate-desc-input" />
                  <div className="grid grid-cols-2 gap-3">
                    <input value={candidateForm.youtube_url} onChange={e => setCandidateForm(f => ({ ...f, youtube_url: e.target.value }))}
                      placeholder="Lien vidéo YouTube (optionnel)" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="candidate-youtube-input" />
                    <input value={candidateForm.links} onChange={e => setCandidateForm(f => ({ ...f, links: e.target.value }))}
                      placeholder="Liens externes (optionnel)" className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  <button onClick={addCandidate} className="flex items-center gap-1.5 px-4 py-2 bg-[#35AFE4] text-white rounded-lg text-xs font-bold hover:bg-[#1e9bd4] transition-colors" data-testid="add-candidate-btn">
                    <Plus size={14} /> Ajouter ce candidat
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Annuler</button>
              <button onClick={handleSave} className="px-6 py-2 bg-[#35AFE4] text-white rounded-lg text-sm font-semibold hover:bg-[#1e9bd4]" data-testid="save-election-btn">
                {editing ? 'Enregistrer' : 'Créer le vote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {resultsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResultsModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Résultats</h2>
                <p className="text-xs text-gray-500">{resultsModal.election?.title} | {resultsModal.total_voters} votant(s)</p>
              </div>
              <button onClick={() => setResultsModal(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {resultsModal.results?.map((r, i) => {
                const maxV = resultsModal.results[0]?.votes || 1;
                const pct = resultsModal.total_voters > 0 ? Math.round((r.votes / resultsModal.total_voters) * 100) : 0;
                return (
                  <div key={r.candidate_id} className="flex items-center gap-3" data-testid={`result-${i}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i < 3 ? 'bg-[#35AFE4] text-white' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
                      {r.photo_url ? <img src={r.photo_url} alt={r.name} className="w-full h-full object-cover" /> : <span className="text-white text-xs font-bold">{r.name[0]}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-800">{r.name}</span>
                        <span className="text-xs font-bold text-[#35AFE4]">{r.votes} voix ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#35AFE4] to-[#1e3a5f] transition-all duration-500" style={{ width: `${maxV > 0 ? (r.votes / maxV) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default ElectionsManagementPage;
